import {
    AddMemberRequest, MemberProfileRequest, ResetPasswordRequest,
    SignupRequest,
    UpdateGeneralSettingRequest, UpdateMemberRequest,
} from '@/types/request';
import { QueryRecordsRequest, ApiRequest } from '@/types/request';
import { ObjectFactory } from '@/config/objectFactory';
import { MedTransGoConstants } from '@/util/constants';
import { ApiResponse, MemberProfile, MemberRequestsCountResponse } from '@/types/response';
import { CommonUtils } from '@/util/commonUtils';
import { IMemberService } from '../iMemberService';
import { AccessKey, Organization } from '@/types/entity';


export class MemberService implements IMemberService {
    public async getMemberList(baseURL: string, searchString?: string, start?: number):
        Promise<ApiResponse<MemberProfile[]>> {
        const iHttpService = ObjectFactory.getHttpService();
        const queryRecordsRequest: QueryRecordsRequest = {
            limit: MedTransGoConstants.PAGINATION_LIMIT,
            start,
        };
        const apiRequest: ApiRequest = {
            baseURL,
            data: queryRecordsRequest,
            url: `${MedTransGoConstants.HTTP_URL_MEMBERS}/list`,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.post<MemberProfile[]>(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async getMemberDetails(baseURL: string, memberId: string): Promise<ApiResponse<MemberProfile>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_MEMBERS}/${memberId}`,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.get<MemberProfile>(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async addMember(baseURL: string, addMemberRequest: AddMemberRequest): Promise<ApiResponse<any>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_MEMBERS}`,
            data: addMemberRequest,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.post<AddMemberRequest>(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async deleteMember(baseURL: string, member: MemberProfile): Promise<ApiResponse<any>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_MEMBERS}/${member.memberId}`,
        };
        const response = await iHttpService.delete<any>(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async updateMember(baseURL: string, memberId: string, updateMemberRequest:
        UpdateMemberRequest): Promise<ApiResponse<any>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_MEMBERS}/${memberId}`,
            data: updateMemberRequest,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.put<UpdateMemberRequest>(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }
    public async updateMemberSettings(baseURL: string, userId: string, updateGeneralSettingRequest:
        UpdateGeneralSettingRequest): Promise<ApiResponse<any>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_MEMBERS}/${userId}/settings`,
            data: updateGeneralSettingRequest,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.put(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }
    public async resetPassword(baseURL: string, userId: string, resetPasswordRequest:
        ResetPasswordRequest): Promise<ApiResponse<any>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_MEMBERS}/${userId}/resetPassword`,
            data: resetPasswordRequest,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.put<ResetPasswordRequest>(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async saveMemberProfile(baseURL: string, memberRequest: MemberProfileRequest): Promise<ApiResponse<any>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: MedTransGoConstants.HTTP_URL_MEMBERS,
            data: memberRequest,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.post<MemberProfileRequest>(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async doSignup(baseURL: string, signupRequest: SignupRequest): Promise<ApiResponse<any>> {
        const iHttpService = ObjectFactory.getHttpService();
        const request: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_MEMBERS}/signup`,
            data: signupRequest,
            loaderEvent: {
                show: true,
                message: 'Signing up',
            },
        };
        const response = await iHttpService.post(request);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async getAccessKeyList(baseURL: string, start?: number):
        Promise<ApiResponse<AccessKey[]>> {
        const iHttpService = ObjectFactory.getHttpService();
        const queryRecordsRequest: QueryRecordsRequest = {
            conditions: [],
            limit: MedTransGoConstants.PAGINATION_LIMIT,
            start: start ? start : 0,
        };
        const apiRequest: ApiRequest = {
            baseURL,
            data: queryRecordsRequest,
            url: `${MedTransGoConstants.HTTP_URL_ACCESS_KEYS}/list`,
            loaderEvent: {
                show: true,
            },
        };
        const response = await ObjectFactory.getHttpService().post<AccessKey[]>(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async getMemberRequestCount(baseURL: string): Promise<ApiResponse<MemberRequestsCountResponse>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_MEMBERS}/request/count`,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.get<MemberRequestsCountResponse>(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async getOrganizationById(id: string, baseURL: string, showLoader?: boolean):
        Promise<ApiResponse<Organization>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_ORGANIZATIONS}/${id}`,
        };
        if (showLoader === undefined || null) {
            apiRequest.loaderEvent = {
                show: true,
            };
        }
        const response = await iHttpService.get<Organization>(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }


}
